import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Link } from "react-router-dom";
import { AdminPageHeader, Card, Spinner, ErrorBanner, Btn, Tag, EmptyState } from "./_shared";
import { Plus, ExternalLink, BarChart3, Pause, Play, Archive as ArchiveIcon, Star, X as XIcon, Rocket } from "lucide-react";
import LaunchKitModal from "./LaunchKitModal";

const Pill = ({ children, color = "#FF9933" }) => (
  <span className="text-[10px] uppercase font-extrabold tracking-[0.25em] px-2 py-1 border" style={{ borderColor: color, color, background: "transparent" }}>
    {children}
  </span>
);

const StatusBadge = ({ status }) => {
  const map = {
    active: { color: "#22c55e", label: "Active" },
    paused: { color: "#f59e0b", label: "Paused" },
    draft: { color: "#a3a3a3", label: "Draft" },
    closed: { color: "#0ea5e9", label: "Closed" },
    archived: { color: "#737373", label: "Archived" },
  };
  const cfg = map[status] || { color: "#a3a3a3", label: status || "—" };
  return <Pill color={cfg.color}>{cfg.label}</Pill>;
};

export const ActiveQuestions = () => {
  const [polls, setPolls] = useState(null);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  // Launch kit either takes a `kit` payload (fresh from create) or a `slug`
  // (re-opened from a poll row, fetched lazily).
  const [launchKit, setLaunchKit] = useState(null); // { slug, prefetched? }

  const fetchPolls = () => {
    setErr(null);
    adminApi.get("/admin/polls")
      .then((r) => setPolls(r.data?.polls || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load polls."));
  };

  useEffect(() => { fetchPolls(); }, []);

  const handleStatusChange = async (slug, status) => {
    try {
      await adminApi.post(`/admin/polls/${slug}/status`, { status });
      fetchPolls();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Status change failed.");
    }
  };

  return (
    <div className="space-y-6" data-testid="active-questions-page">
      <AdminPageHeader
        eyebrow="Active Questions"
        title="Civic Polls"
        sub="Every campaign that Kerala Speaks is running right now. Create new polls, pause them anytime, or archive once a campaign has closed."
        action={
          <Btn onClick={() => setCreating(true)} data-testid="add-new-poll-btn">
            <Plus size={12} strokeWidth={3} />
            Add new poll
          </Btn>
        }
      />

      {err && <ErrorBanner message={err} />}

      {polls === null && !err && <Spinner label="Loading polls…" />}

      {polls !== null && polls.length === 0 && (
        <EmptyState
          title="No polls yet."
          sub="Tap “Add new poll” to launch your first civic question."
        />
      )}

      {polls !== null && polls.length > 0 && (
        <div className="space-y-4" data-testid="polls-list">
          {polls.map((p) => (
            <PollRow
              key={p.slug}
              poll={p}
              onStatus={handleStatusChange}
              onLaunchKit={() => setLaunchKit({ slug: p.slug })}
            />
          ))}
        </div>
      )}

      {creating && (
        <CreatePollDrawer
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            fetchPolls();
            // If the API returned an immediate launch_kit (always true now),
            // pop it open so the admin can copy captions / download the
            // poster without an extra click.
            if (created?.launch_kit) {
              setLaunchKit({ slug: created.slug, prefetched: created.launch_kit });
            }
          }}
        />
      )}

      {launchKit && (
        <LaunchKitModal
          slug={launchKit.slug}
          prefetched={launchKit.prefetched}
          onClose={() => setLaunchKit(null)}
        />
      )}
    </div>
  );
};

const PollRow = ({ poll, onStatus, onLaunchKit }) => {
  const t = poll.totals || {};
  const isMC = poll.poll_type === "multiple_choice";
  return (
    <Card testId={`poll-row-${poll.slug}`} title={null}>
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Pill color={isMC ? "#FF9933" : "#9bc6ff"}>{isMC ? "Multiple Choice" : "Open Conversation"}</Pill>
            <StatusBadge status={poll.status} />
            {poll.category && <Pill color="#FDFBF7">{poll.category}</Pill>}
            {poll.featured && (
              <Pill color="#FF9933">
                <Star size={9} strokeWidth={3} style={{ display: "inline", marginRight: 4 }} /> Featured
              </Pill>
            )}
          </div>
          <h3 className="font-display uppercase text-base md:text-lg leading-tight text-[#FDFBF7]" data-testid={`poll-row-title-${poll.slug}`}>
            {poll.title || poll.headline}
          </h3>
          <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{poll.headline}</p>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {isMC ? (
              <>
                <span data-testid={`poll-row-votes-${poll.slug}`}>{(t.votes || 0).toLocaleString()} votes</span>
                <span>{(t.comments_approved || 0).toLocaleString()} comments</span>
                <span>{(poll.options || []).length} options</span>
              </>
            ) : (
              <>
                <span data-testid={`poll-row-messages-${poll.slug}`}>{(t.messages || 0).toLocaleString()} messages</span>
                <span>{(t.upvotes || 0).toLocaleString()} upvotes</span>
              </>
            )}
            <span>Slug: {poll.slug}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0 self-start">
          <Btn variant="ghost" onClick={onLaunchKit} data-testid={`poll-row-launch-${poll.slug}`}>
            <Rocket size={11} strokeWidth={3} /> Launch Kit
          </Btn>
          <Link to={isMC ? `/p/${poll.slug}` : "/"} target="_blank" rel="noopener noreferrer">
            <Btn variant="ghost" data-testid={`poll-row-view-${poll.slug}`}>
              <ExternalLink size={11} strokeWidth={3} /> View
            </Btn>
          </Link>
          <Link to={`/admin/reports`}>
            <Btn variant="ghost" data-testid={`poll-row-report-${poll.slug}`}>
              <BarChart3 size={11} strokeWidth={3} /> Report
            </Btn>
          </Link>
          {poll.status === "active" ? (
            <Btn variant="ghost" onClick={() => onStatus(poll.slug, "paused")} data-testid={`poll-row-toggle-${poll.slug}`} data-state="active">
              <Pause size={11} strokeWidth={3} /> Pause
            </Btn>
          ) : (
            <Btn variant="ghost" onClick={() => onStatus(poll.slug, "active")} data-testid={`poll-row-toggle-${poll.slug}`} data-state="paused">
              <Play size={11} strokeWidth={3} /> Activate
            </Btn>
          )}
          {poll.status !== "archived" && (
            <Btn variant="ghost" onClick={() => onStatus(poll.slug, "archived")} data-testid={`poll-row-archive-${poll.slug}`}>
              <ArchiveIcon size={11} strokeWidth={3} /> Archive
            </Btn>
          )}
        </div>
      </div>
    </Card>
  );
};

const blankOption = () => ({ label: "", label_ml: "" });

const CreatePollDrawer = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    poll_type: "multiple_choice",
    title: "",
    title_ml: "",
    headline: "",
    headline_ml: "",
    subtext: "",
    subtext_ml: "",
    notice: "",
    notice_ml: "",
    category: "",
    category_ml: "",
    tags_csv: "",
    follow_up_question: "",
    follow_up_question_ml: "",
    closing_date: "",
    featured: false,
    status: "active",
    options: [blankOption(), blankOption()],
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updOpt = (i, k, v) => setForm((f) => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));
  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, blankOption()] }));
  const rmOpt = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const payload = {
        poll_type: form.poll_type,
        title: form.title.trim(),
        title_ml: form.title_ml.trim() || undefined,
        headline: form.headline.trim(),
        headline_ml: form.headline_ml.trim() || undefined,
        subtext: form.subtext.trim() || undefined,
        subtext_ml: form.subtext_ml.trim() || undefined,
        notice: form.notice.trim() || undefined,
        notice_ml: form.notice_ml.trim() || undefined,
        category: form.category.trim() || undefined,
        category_ml: form.category_ml.trim() || undefined,
        tags: form.tags_csv ? form.tags_csv.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        follow_up_question: form.follow_up_question.trim() || undefined,
        follow_up_question_ml: form.follow_up_question_ml.trim() || undefined,
        closing_date: form.closing_date ? new Date(form.closing_date).toISOString() : undefined,
        featured: form.featured,
        status: form.status,
      };
      if (form.poll_type === "multiple_choice") {
        const opts = form.options
          .filter((o) => o.label.trim())
          .map((o) => ({ label: o.label.trim(), label_ml: o.label_ml.trim() || undefined }));
        if (opts.length < 2) throw new Error("Add at least 2 options with labels.");
        payload.options = opts;
      }
      const r = await adminApi.post("/admin/polls", payload);
      onCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not create poll.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="create-poll-drawer"
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto border-l-2 border-[#1f1f1f]"
        style={{ background: "#0F0F0F" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b-2 border-[#1f1f1f]" style={{ background: "#0F0F0F" }}>
          <div>
            <p className="text-[10px] uppercase font-extrabold tracking-[0.4em]" style={{ color: "#FF9933" }}>New Poll</p>
            <h2 className="font-display uppercase text-xl text-[#FDFBF7] mt-1">Add new poll</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:opacity-80" data-testid="create-poll-close">
            <XIcon size={20} strokeWidth={3} color="#FDFBF7" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="create-poll-form">
          {err && <ErrorBanner message={err} />}

          <Section title="Basics">
            <Field label="Poll type">
              <select
                value={form.poll_type}
                onChange={(e) => upd("poll_type", e.target.value)}
                className="border-2 border-[#1f1f1f] px-3 py-2 bg-[#0A0A0A] text-[#FDFBF7] w-full"
                data-testid="create-poll-type"
              >
                <option value="multiple_choice">Multiple Choice (e.g. Stray Dogs)</option>
                <option value="open">Open Conversation (free text + upvote)</option>
              </select>
            </Field>
            <Field label="Title *">
              <Input value={form.title} onChange={(v) => upd("title", v)} placeholder="e.g. Public Transport for Kerala" testId="create-poll-title" required />
            </Field>
            <Field label="Title (Malayalam) — optional"><Input value={form.title_ml} onChange={(v) => upd("title_ml", v)} testId="create-poll-title-ml" /></Field>
            <Field label="Headline (the question) *">
              <Input value={form.headline} onChange={(v) => upd("headline", v)} placeholder="What should Kerala prioritise for public transport?" testId="create-poll-headline" required />
            </Field>
            <Field label="Headline (Malayalam) — optional"><Input value={form.headline_ml} onChange={(v) => upd("headline_ml", v)} testId="create-poll-headline-ml" /></Field>
            <Field label="Subtext (context paragraph) — optional">
              <TextArea value={form.subtext} onChange={(v) => upd("subtext", v)} testId="create-poll-subtext" />
            </Field>
            <Field label="Subtext (Malayalam) — optional"><TextArea value={form.subtext_ml} onChange={(v) => upd("subtext_ml", v)} testId="create-poll-subtext-ml" /></Field>
            <Field label="Moderation notice — optional">
              <Input value={form.notice} onChange={(v) => upd("notice", v)} placeholder="Please keep discussions respectful." testId="create-poll-notice" />
            </Field>
            <Field label="Notice (Malayalam) — optional"><Input value={form.notice_ml} onChange={(v) => upd("notice_ml", v)} testId="create-poll-notice-ml" /></Field>
          </Section>

          <Section title="Tagging">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category"><Input value={form.category} onChange={(v) => upd("category", v)} placeholder="Public Safety" testId="create-poll-category" /></Field>
              <Field label="Category (Malayalam)"><Input value={form.category_ml} onChange={(v) => upd("category_ml", v)} testId="create-poll-category-ml" /></Field>
            </div>
            <Field label="Tags (comma separated)">
              <Input value={form.tags_csv} onChange={(v) => upd("tags_csv", v)} placeholder="Kerala, Public Voice, Healthcare" testId="create-poll-tags" />
            </Field>
          </Section>

          {form.poll_type === "multiple_choice" && (
            <Section
              title="Options"
              hint="Add at least 2. Voters pick exactly one."
              action={(
                <button type="button" onClick={addOpt} className="text-[10px] uppercase font-extrabold tracking-[0.3em]" style={{ color: "#FF9933" }} data-testid="create-poll-add-option">
                  + Add option
                </button>
              )}
            >
              <div className="space-y-3" data-testid="create-poll-options">
                {form.options.map((opt, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                    <Input
                      value={opt.label}
                      onChange={(v) => updOpt(i, "label", v)}
                      placeholder={`Option ${i + 1} label`}
                      testId={`create-poll-opt-label-${i}`}
                    />
                    <Input
                      value={opt.label_ml}
                      onChange={(v) => updOpt(i, "label_ml", v)}
                      placeholder="Malayalam (optional)"
                      testId={`create-poll-opt-label-ml-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => rmOpt(i)}
                      disabled={form.options.length <= 2}
                      title={form.options.length <= 2 ? "Minimum 2 options" : "Remove option"}
                      className="px-2 py-2 text-neutral-500 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`create-poll-opt-rm-${i}`}
                    >
                      <XIcon size={14} strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Follow-up (after voting)">
            <Field label="Follow-up question — optional">
              <Input value={form.follow_up_question} onChange={(v) => upd("follow_up_question", v)} testId="create-poll-followup" />
            </Field>
            <Field label="Follow-up (Malayalam)"><Input value={form.follow_up_question_ml} onChange={(v) => upd("follow_up_question_ml", v)} testId="create-poll-followup-ml" /></Field>
          </Section>

          <Section title="Schedule & Visibility">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Closing date — optional">
                <input
                  type="datetime-local"
                  value={form.closing_date}
                  onChange={(e) => upd("closing_date", e.target.value)}
                  className="border-2 border-[#1f1f1f] px-3 py-2 bg-[#0A0A0A] text-[#FDFBF7] w-full"
                  data-testid="create-poll-closing"
                />
              </Field>
              <Field label="Initial status">
                <select
                  value={form.status}
                  onChange={(e) => upd("status", e.target.value)}
                  className="border-2 border-[#1f1f1f] px-3 py-2 bg-[#0A0A0A] text-[#FDFBF7] w-full"
                  data-testid="create-poll-status"
                >
                  <option value="active">Active (publish immediately)</option>
                  <option value="draft">Draft (hide from public)</option>
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300 mt-2">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => upd("featured", e.target.checked)}
                data-testid="create-poll-featured"
              />
              Feature in homepage hero slider
            </label>
          </Section>

          <div className="flex gap-3 pt-2 sticky bottom-0 py-3" style={{ background: "linear-gradient(to top, #0F0F0F 70%, transparent)" }}>
            <Btn type="submit" disabled={submitting} data-testid="create-poll-submit">
              {submitting ? "Creating…" : "Create poll →"}
            </Btn>
            <Btn type="button" variant="ghost" onClick={onClose} data-testid="create-poll-cancel">Cancel</Btn>
          </div>
        </form>
      </div>
    </div>
  );
};

const Section = ({ title, hint, action, children }) => (
  <div className="border-2 border-[#1f1f1f] p-4" style={{ background: "#141414" }}>
    <div className="flex items-center justify-between mb-3 gap-3">
      <div>
        <p className="text-[10px] uppercase font-extrabold tracking-[0.35em]" style={{ color: "#FF9933" }}>{title}</p>
        {hint && <p className="text-[10px] text-neutral-500 mt-1">{hint}</p>}
      </div>
      {action}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Input = ({ value, onChange, placeholder, testId, required, type = "text" }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    required={required}
    data-testid={testId}
    className="border-2 border-[#1f1f1f] px-3 py-2 bg-[#0A0A0A] text-[#FDFBF7] w-full placeholder:text-neutral-600 focus:border-[#FF9933] outline-none"
  />
);

const TextArea = ({ value, onChange, testId, rows = 3 }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    data-testid={testId}
    className="border-2 border-[#1f1f1f] px-3 py-2 bg-[#0A0A0A] text-[#FDFBF7] w-full placeholder:text-neutral-600 focus:border-[#FF9933] outline-none resize-y"
  />
);

export default ActiveQuestions;
